const API_URL = process.env.REACT_APP_BACKEND_URL || '';

class ApiService {
  constructor() {
    this.baseUrl = `${API_URL}/api`;
  }

  getToken() {
    return localStorage.getItem('sip_token');
  }

  setToken(token) {
    localStorage.setItem('sip_token', token);
  }

  clearToken() {
    localStorage.removeItem('sip_token');
    localStorage.removeItem('sip_user');
    localStorage.removeItem('sip_school');
  }

  getUser() {
    const user = localStorage.getItem('sip_user');
    return user ? JSON.parse(user) : null;
  }

  setUser(user) {
    localStorage.setItem('sip_user', JSON.stringify(user));
  }

  getSchool() {
    const school = localStorage.getItem('sip_school');
    return school ? JSON.parse(school) : null;
  }

  setSchool(school) {
    localStorage.setItem('sip_school', JSON.stringify(school));
  }

  async request(endpoint, options = {}) {
    const token = this.getToken();
    const headers = {
      ...options.headers,
    };

    // Don't set Content-Type for FormData (browser sets it with boundary)
    if (!options.isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const { isFormData, ...fetchOptions } = options;

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...fetchOptions,
      headers,
    });

    if (response.status === 401 && !endpoint.includes('/auth/login')) {
      this.clearToken();
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      // Surface structured detail (string OR object with code/message)
      let message = 'Request failed';
      let detail = null;
      if (typeof error.detail === 'string') {
        message = error.detail;
      } else if (error.detail && typeof error.detail === 'object') {
        message = error.detail.message || JSON.stringify(error.detail);
        detail = error.detail;
      }
      const err = new Error(message);
      err.status = response.status;
      err.detail = detail;
      err.code = detail?.code;
      throw err;
    }

    return response.json();
  }

  // Auth APIs
  async login(schoolCode, role, email, password) {
    const data = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ schoolCode, role, email, password }),
    });
    
    this.setToken(data.token);
    this.setUser(data.user);
    this.setSchool(data.school);
    
    return data;
  }

  // Individual (B2C) login — email + userCode + password
  async loginIndividual(email, userCode, password, deviceId) {
    const data = await this.request('/auth/login-individual', {
      method: 'POST',
      body: JSON.stringify({ email, userCode, password, deviceId }),
    });
    this.setToken(data.token);
    this.setUser(data.user);
    this.setSchool(null);
    return data;
  }

  async signupIndividual(payload) {
    // Auto-attach the persistent device fingerprint so the backend can enforce
    // the "one free account per device" cap server-side. Caller-provided
    // deviceId still wins if explicitly set.
    const deviceId = payload?.deviceId || this._getOrCreateDeviceId();
    return this.request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ ...payload, deviceId }),
    });
  }

  async forgotPassword(email) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resendUserCode(email) {
    return this.request('/auth/resend-user-code', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token, newPassword) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  // Generates a stable per-device fingerprint stored in localStorage. Used by
  // Google sign-up to enforce one Free-tier account per device.
  _getOrCreateDeviceId() {
    let id = localStorage.getItem('sip_device_id');
    if (!id) {
      id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'dev_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('sip_device_id', id);
    }
    return id;
  }

  // Custom Google OAuth — server-side Authorization Code flow.
  // The backend at /api/auth/google/callback finishes the exchange and
  // redirects the browser back to /login with the JWT in the URL
  // fragment. This helper just persists what the backend already minted.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  async adoptGoogleSession(token, user) {
    if (!token || !user) throw new Error('Missing Google session payload');
    this.setToken(token);
    this.setUser(user);
    this.setSchool(null);
    return { token, user };
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async changePassword(oldPassword, newPassword) {
    return this.request(`/auth/change-password?old_password=${encodeURIComponent(oldPassword)}&new_password=${encodeURIComponent(newPassword)}`, {
      method: 'POST',
    });
  }

  logout() {
    this.clearToken();
    window.location.href = '/login';
  }

  // School APIs (Super Admin)
  async createSchool(schoolData) {
    return this.request('/schools', {
      method: 'POST',
      body: JSON.stringify(schoolData),
    });
  }

  async listSchools() {
    return this.request('/schools');
  }

  async getSchoolById(schoolId) {
    return this.request(`/schools/${schoolId}`);
  }

  async updateSchool(schoolId, updates) {
    return this.request(`/schools/${schoolId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // User APIs
  async createUser(userData) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async listUsers(role = null) {
    const query = role ? `?role=${role}` : '';
    return this.request(`/users${query}`);
  }

  async getUserById(userId) {
    return this.request(`/users/${userId}`);
  }

  async updateUser(userId, updates) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteUser(userId) {
    return this.request(`/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Student APIs
  async createStudent(studentData) {
    return this.request('/students', {
      method: 'POST',
      body: JSON.stringify(studentData),
    });
  }

  async listStudents(filters = {}) {
    const params = new URLSearchParams();
    if (filters.class) params.append('class', filters.class);
    if (filters.section) params.append('section', filters.section);
    if (filters.status) params.append('status', filters.status);
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/students${query}`);
  }

  async getStudentById(studentId) {
    return this.request(`/students/${studentId}`);
  }

  async updateStudent(studentId, updates) {
    return this.request(`/students/${studentId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async archiveStudent(studentId, reason = null) {
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    return this.request(`/students/${studentId}${query}`, {
      method: 'DELETE',
    });
  }

  async restoreStudent(studentId) {
    return this.request(`/students/${studentId}/restore`, {
      method: 'POST',
    });
  }

  async sendStudentEmail(studentId, subject, message) {
    return this.request(`/students/${studentId}/send-email`, {
      method: 'POST',
      body: JSON.stringify({ subject, message }),
    });
  }

  async bulkUploadStudents(file) {
    const token = this.getToken();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${this.baseUrl}/students/bulk-upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (response.status === 401) {
      this.clearToken();
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }

    return response.json();
  }

  // Attendance APIs
  async markAttendance(attendanceData) {
    return this.request('/attendance', {
      method: 'POST',
      body: JSON.stringify(attendanceData),
    });
  }

  async getClassAttendance(studentClass, section, date) {
    return this.request(`/attendance/class?class=${studentClass}&section=${section}&date=${date}`);
  }

  async getStudentAttendance(studentId, month = null) {
    const query = month ? `?month=${month}` : '';
    return this.request(`/attendance/student/${studentId}${query}`);
  }

  // Notification APIs
  async getNotifications(unreadOnly = false) {
    const query = unreadOnly ? '?unread_only=true' : '';
    return this.request(`/notifications${query}`);
  }

  async markNotificationRead(notificationId) {
    return this.request(`/notifications/${notificationId}/read`, {
      method: 'PUT',
    });
  }

  async markAllNotificationsRead() {
    return this.request('/notifications/read-all', {
      method: 'PUT',
    });
  }

  // Dashboard APIs
  async getDashboardStats() {
    return this.request('/dashboard/stats');
  }

  // Audit Log APIs
  async getAuditLogs(entity = null, entityId = null, limit = 100) {
    const params = new URLSearchParams();
    if (entity) params.append('entity', entity);
    if (entityId) params.append('entity_id', entityId);
    params.append('limit', limit.toString());
    
    return this.request(`/audit-logs?${params.toString()}`);
  }

  // File APIs
  async uploadFile(file, linkedEntity = null, linkedEntityId = null) {
    const formData = new FormData();
    formData.append('file', file);
    if (linkedEntity) formData.append('linked_entity', linkedEntity);
    if (linkedEntityId) formData.append('linked_entity_id', linkedEntityId);

    const token = this.getToken();
    const response = await fetch(`${this.baseUrl}/files/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }

    return response.json();
  }

  async getFile(fileId) {
    return this.request(`/files/${fileId}`);
  }

  // Finance APIs
  async createFeeStructure(data) {
    return this.request('/finance/fee-structures', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async listFeeStructures(studentClass = null) {
    const query = studentClass ? `?class=${studentClass}` : '';
    return this.request(`/finance/fee-structures${query}`);
  }

  async updateFeeStructure(feeId, updates) {
    return this.request(`/finance/fee-structures/${feeId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteFeeStructure(feeId) {
    return this.request(`/finance/fee-structures/${feeId}`, {
      method: 'DELETE',
    });
  }

  async createInvoice(data) {
    return this.request('/finance/invoices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async listInvoices(filters = {}) {
    const params = new URLSearchParams();
    if (filters.studentId) params.append('student_id', filters.studentId);
    if (filters.status) params.append('status', filters.status);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/finance/invoices${query}`);
  }

  async recordPayment(data) {
    return this.request('/finance/payments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async listPayments(filters = {}) {
    const params = new URLSearchParams();
    if (filters.invoiceId) params.append('invoice_id', filters.invoiceId);
    if (filters.studentId) params.append('student_id', filters.studentId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/finance/payments${query}`);
  }

  async getFinanceSummary() {
    return this.request('/finance/summary');
  }

  async generateInvoicesForClass(studentClass, feeStructureId, dueDate) {
    return this.request(`/finance/generate-invoices?class=${studentClass}&fee_structure_id=${feeStructureId}&due_date=${dueDate}`, {
      method: 'POST',
    });
  }

  async getInvoiceShareInfo(invoiceId) {
    return this.request(`/finance/invoices/${invoiceId}/share-info`);
  }

  // Razorpay APIs
  async getRazorpayConfig() {
    return this.request('/finance/razorpay/config');
  }

  async createRazorpayOrder(invoiceId) {
    return this.request('/finance/razorpay/create-order', {
      method: 'POST',
      body: JSON.stringify({ invoiceId }),
    });
  }

  async verifyRazorpayPayment(data) {
    return this.request('/finance/razorpay/verify-payment', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Invoice Email API
  async sendInvoiceEmail(invoiceId) {
    return this.request(`/finance/invoices/${invoiceId}/send-email`, {
      method: 'POST',
    });
  }

  // Parent Portal APIs
  async getParentDashboard() {
    return this.request('/parent/dashboard');
  }

  async getParentChildren() {
    return this.request('/parent/children');
  }

  async getParentChildAttendance(studentId, month = null) {
    const query = month && month !== 'all' ? `?month=${month}` : '';
    return this.request(`/parent/children/${studentId}/attendance${query}`);
  }

  async getParentChildFees(studentId) {
    return this.request(`/parent/children/${studentId}/fees`);
  }

  // Teachers APIs
  async listTeachers() {
    return this.request('/teachers');
  }

  async getTeacherStats() {
    return this.request('/teachers/stats');
  }

  // Analytics API
  async getAnalyticsOverview() {
    return this.request('/analytics/overview');
  }

  // Teacher Management APIs
  async createTeacher(data) {
    return this.request('/teachers', { method: 'POST', body: JSON.stringify(data) });
  }

  async getTeacherProfile(teacherId) {
    return this.request(`/teachers/${teacherId}`);
  }

  async updateTeacher(teacherId, data) {
    return this.request(`/teachers/${teacherId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async uploadTeacherDocument(teacherId, docType, file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request(`/teachers/${teacherId}/upload?doc_type=${docType}`, {
      method: 'POST',
      body: formData,
      isFormData: true,
    });
  }

  async deleteDocument(fileId) {
    return this.request(`/documents/${fileId}`, { method: 'DELETE' });
  }

  // Class Assignment APIs
  async getClassAssignments() {
    return this.request('/classes/assignments');
  }

  async assignTeacherToClass(teacherId, classSection, action = 'assign') {
    return this.request('/classes/assign-teacher', {
      method: 'POST',
      body: JSON.stringify({ teacherId, classSection, action }),
    });
  }

  // Attendance Percentage by Period
  async getAttendancePercentage(period = 'month') {
    return this.request(`/attendance/percentage?period=${period}`);
  }

  // Finance Collection by Period
  async getFeeCollection(period = 'monthly') {
    return this.request(`/finance/collection?period=${period}`);
  }

  // Global Search
  async search(query) {
    return this.request(`/search?q=${encodeURIComponent(query)}`);
  }

  // Student Profile with Attendance Summary
  async getStudentProfile(studentId) {
    return this.request(`/students/${studentId}/profile`);
  }

  // Attendance Oversight (Admin)
  async getAttendanceOversight(params = {}) {
    const qs = new URLSearchParams();
    if (params.class) qs.append('class', params.class);
    if (params.section) qs.append('section', params.section);
    if (params.date) qs.append('date', params.date);
    return this.request(`/attendance/oversight?${qs.toString()}`);
  }

  // Get all class-sections
  async getClassSections() {
    return this.request('/classes/sections');
  }

  // Leave Management
  async applyLeave(leaveData) {
    return this.request('/leaves', { method: 'POST', body: JSON.stringify(leaveData) });
  }

  async getLeaves(status = null) {
    const qs = status ? `?status=${status}` : '';
    return this.request(`/leaves${qs}`);
  }

  async approveLeave(leaveId, action, remarks = null) {
    const qs = `?action=${action}${remarks ? `&remarks=${encodeURIComponent(remarks)}` : ''}`;
    return this.request(`/leaves/${leaveId}/approve${qs}`, { method: 'PUT' });
  }

  // Student Documents
  async uploadStudentDocument(studentId, docType, file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request(`/students/${studentId}/documents?doc_type=${encodeURIComponent(docType)}`, {
      method: 'POST',
      body: formData,
      isFormData: true,
    });
  }

  async getStudentDocuments(studentId) {
    return this.request(`/students/${studentId}/documents`);
  }

  // Teacher Schedule
  async getTeacherSchedule(period = 'today') {
    return this.request(`/teachers/schedule?period=${period}`);
  }

  // Timetable
  async getTimetable(params = {}) {
    const qs = new URLSearchParams();
    if (params.classSection) qs.append('classSection', params.classSection);
    if (params.dayOfWeek) qs.append('dayOfWeek', params.dayOfWeek);
    if (params.teacherId) qs.append('teacherId', params.teacherId);
    return this.request(`/timetable?${qs.toString()}`);
  }

  async saveTimetable(entries) {
    return this.request('/timetable', { method: 'POST', body: JSON.stringify({ entries }) });
  }

  async deleteTimetableEntry(classSection, dayOfWeek, periodNumber) {
    return this.request(`/timetable?classSection=${classSection}&dayOfWeek=${dayOfWeek}&periodNumber=${periodNumber}`, { method: 'DELETE' });
  }

  async getTimetableConfig() {
    return this.request('/timetable/config');
  }

  async updateTimetableConfig(config) {
    return this.request('/timetable/config', { method: 'PUT', body: JSON.stringify(config) });
  }

  // Substitutes
  async assignSubstitute(data) {
    return this.request('/substitutes', { method: 'POST', body: JSON.stringify(data) });
  }

  async getSubstitutes(params = {}) {
    const qs = new URLSearchParams();
    if (params.date) qs.append('date', params.date);
    if (params.classSection) qs.append('classSection', params.classSection);
    return this.request(`/substitutes?${qs.toString()}`);
  }

  async removeSubstitute(substituteId) {
    return this.request(`/substitutes/${substituteId}`, { method: 'DELETE' });
  }

  async getAvailableSubstitutes(date, periodNumber, classSection) {
    return this.request(`/substitutes/available?date=${date}&periodNumber=${periodNumber}&classSection=${encodeURIComponent(classSection)}`);
  }

  async getSubstituteReport() {
    return this.request('/substitutes/report');
  }

  // ─── User Preferences (Language & Localization Phase 1) ───
  async getMyPreferences() {
    return this.request('/preferences/me');
  }

  async updateMyPreferences(payload) {
    return this.request('/preferences/me', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  // ─── Super Admin platform metrics & user drill-down ───
  async getPlatformMetrics() {
    return this.request('/super-admin/platform-metrics');
  }
  async getRegisteredUsers(period = 'all') {
    return this.request(`/super-admin/registered-users?period=${encodeURIComponent(period)}`);
  }
}

export const api = new ApiService();
export default api;
